import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, Calendar, Phone, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { API_URL } from '../lib/utils';

export default function ConfirmAppointmentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [error, setError] = useState(null);

  const fetchAppointment = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/public/appointments/confirm/${token}`);
      const data = await response.json();
      
      if (response.ok) {
        setStatus(data.status);
        setAppointment(data.appointment);
      } else {
        setError(data.detail || 'Appointment not found');
      }
    } catch (err) {
      setError('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const confirmAppointment = async () => {
    setConfirming(true);
    try {
      const response = await fetch(`${API_URL}/public/appointments/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_to_calendar: true }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('confirmed');
        setAppointment(data.appointment);
      } else {
        setError(data.detail || 'Failed to confirm appointment');
      }
    } catch (err) {
      setError('Failed to confirm appointment');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-none shadow-lg">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Oops!</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/contact">
              <Button className="bg-orange-600 hover:bg-orange-700">
                Contact Us
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already confirmed
  if (status === 'already_confirmed' || status === 'confirmed') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-none shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Appointment Confirmed!
              </h1>
              <p className="text-slate-600">
                {status === 'already_confirmed' 
                  ? 'This appointment was already confirmed.' 
                  : 'Thank you for confirming your appointment.'}
              </p>
            </div>
            
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-green-800 mb-4">Appointment Details:</h3>
              <div className="space-y-3 text-slate-700">
                <p><strong>Service:</strong> {appointment?.service_needed}</p>
                <p><strong>Date:</strong> {appointment?.confirmed_date || appointment?.offered_date}</p>
                <p><strong>Time:</strong> {appointment?.confirmed_time || appointment?.offered_time}</p>
                <p><strong>Location:</strong> North Bend, Oregon</p>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Reminders:</strong> You will receive email reminders 1 day and 2 hours before your appointment.
              </p>
            </div>
            
            <div className="text-center space-y-3">
              <a 
                href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(appointment?.service_needed + ' at Community Dental Lab')}&dates=${(appointment?.confirmed_date || appointment?.offered_date)?.replace(/-/g, '')}T${(appointment?.confirmed_time || appointment?.offered_time)?.replace(':', '')}00/${(appointment?.confirmed_date || appointment?.offered_date)?.replace(/-/g, '')}T${(appointment?.confirmed_time || appointment?.offered_time)?.replace(':', '')}00&details=Appointment&location=Coos%20Bay,%20Oregon`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="w-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  Add to Google Calendar
                </Button>
              </a>
              <Link to="/">
                <Button className="w-full bg-orange-600 hover:bg-orange-700">
                  Return to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-none shadow-lg">
          <CardContent className="p-8 text-center">
            <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Expired</h1>
            <p className="text-slate-600 mb-6">
              This confirmation link has expired. Please contact us to reschedule your appointment.
            </p>
            <div className="space-y-3">
              <a href="tel:+15415550123">
                <Button className="w-full bg-orange-600 hover:bg-orange-700">
                  <Phone className="w-4 h-4 mr-2" />
                  Call (541) 756-2121
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="w-full">
                  Contact Us Online
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending confirmation
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-none shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <Calendar className="w-16 h-16 text-orange-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Confirm Your Appointment
            </h1>
            <p className="text-slate-600">
              Please review and confirm your appointment details below.
            </p>
          </div>
          
          {/* Urgency Banner */}
          <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4 mb-6 text-center">
            <p className="text-red-800 font-bold text-lg">ACTION REQUIRED</p>
            <p className="text-red-700 text-sm">
              Please confirm this appointment to secure your time slot. Unconfirmed appointments may be given to other patients.
            </p>
          </div>
          
          {/* Appointment Details */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-orange-800 mb-4 text-lg">Proposed Appointment:</h3>
            <div className="space-y-3 text-slate-700">
              <p className="text-lg"><strong>Service:</strong> {appointment?.service_needed}</p>
              <p className="text-lg"><strong>Date:</strong> {appointment?.offered_date}</p>
              <p className="text-lg"><strong>Time:</strong> {appointment?.offered_time}</p>
              <div className="flex items-start gap-2 mt-4 pt-4 border-t border-orange-200">
                <MapPin className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Community Dental Lab, PC</p>
                  <p className="text-sm text-slate-600">North Bend, Oregon</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* What happens next */}
          <div className="bg-slate-100 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-slate-800 mb-2">What happens after you confirm:</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>You'll receive a confirmation email with calendar invite</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Reminder email 1 day before your appointment</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Reminder email 2 hours before your appointment</span>
              </li>
            </ul>
          </div>
          
          {/* Confirm Button */}
          <Button 
            onClick={confirmAppointment}
            disabled={confirming}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
          >
            {confirming ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                CONFIRM MY APPOINTMENT
              </>
            )}
          </Button>
          
          {/* Can't make it */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600 mb-2">Can't make this time?</p>
            <a href="tel:+15415550123" className="text-orange-600 hover:text-orange-700 font-medium">
              Call us at (541) 756-2121 to reschedule
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
